import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Store, Package, Wallet, User, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import useResponsive from '../hooks/useResponsive';
import { c, t, radius, glassClass } from '../theme';
import logo from '../assets/startup/static-logo.png';

// Sidebar widths. Layout.js pads the page by whichever is current, so the
// two must stay in step. Collapsed keeps the icons visible rather than
// hiding navigation entirely.
export const SIDEBAR_WIDTH = 232;
export const SIDEBAR_COLLAPSED = 64;

// Bottom tab bar. Five destinations, always visible, thumb-reachable.
// Notifications live on the bell in each page header rather than taking a
// tab — a tab is for a place you go, not a thing you check.
//
// Icons use Lucide React — consistent stroke weight, optical size, and
// alignment across every tab. No emoji fallbacks.

const CustomerNav = ({ collapsed = false, onToggle }) => {
  const { user } = useAuth();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const [orderCount, setOrderCount] = useState(0);

  const fetchCounts = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { count } = await supabase
        .from('orders')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('seen_by_customer', false);
      if (count !== null) setOrderCount(count);
    } catch (err) {
      // A failed badge count must never break navigation.
    }
  }, [user?.id]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const tabs = [
    { Icon: Home, label: 'Home', path: '/dashboard' },
    { Icon: Store, label: 'Stores', path: '/stores' },
    { Icon: Package, label: 'Orders', path: '/orders', badge: orderCount },
    // LABEL, NOT PATH. The route stays /wallet - bookmarks, ProtectedRoute
    // and StartupFlow all reference it. Only what the customer READS
    // changes, which is the same rule the database follows: the table is
    // still customer_wallets. "Wallet" was the last place the app said the
    // word out loud, on every customer screen.
    { Icon: Wallet, label: 'Credit', path: '/wallet' },
    { Icon: User, label: 'Profile', path: '/profile' },
  ];

  const isActive = (path) => {
    if (path === '/stores') return location.pathname.startsWith('/stores');
    if (path === '/orders') return location.pathname.startsWith('/orders');
    return location.pathname === path;
  };

  // On desktop the tab bar becomes a left sidebar. A bottom bar is a phone
  // pattern: it wastes the vertical space a wide screen has plenty of, and
  // strands navigation at the far edge of the viewport.
  if (!isMobile) {
    return (
      <nav
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: (collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH) + 'px',
          zIndex: 200,
          background: c.surface,
          borderRight: '1px solid ' + c.line,
          display: 'flex',
          flexDirection: 'column',
          padding: collapsed ? '18px 8px' : '18px 14px',
          boxSizing: 'border-box',
          transition: 'width 0.18s ease, padding 0.18s ease',
          overflow: 'hidden',
        }}
      >
        <Link
          to="/dashboard"
          title="Pharvlink"
          style={{
            display: 'flex', alignItems: 'center', gap: '9px',
            textDecoration: 'none', padding: collapsed ? 0 : '0 6px',
            marginBottom: '14px',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <img
            src={logo}
            alt="Pharvlink"
            style={{ width: '30px', height: '30px', objectFit: 'contain', display: 'block', flexShrink: 0 }}
          />
          {!collapsed && (
            <span style={{ ...t.pageTitle, fontSize: '18px', color: c.brandDeep, whiteSpace: 'nowrap' }}>Pharvlink</span>
          )}
        </Link>

        <button
          onClick={onToggle}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          style={{
            display: 'flex', alignItems: 'center', gap: '11px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            width: '100%', padding: '8px 12px', marginBottom: '8px',
            background: 'transparent', border: '1px solid transparent',
            borderRadius: radius.md, cursor: 'pointer',
            fontFamily: t.family,
          }}
        >
          {collapsed
            ? <PanelLeftOpen size={18} strokeWidth={2} color={c.inkMuted} />
            : <PanelLeftClose size={18} strokeWidth={2} color={c.inkMuted} />}
          {!collapsed && (
            <span style={{ ...t.micro, color: c.inkMuted, whiteSpace: 'nowrap' }}>Collapse</span>
          )}
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {tabs.map((tab) => {
            const active = isActive(tab.path);
            return (
              <Link
                key={tab.path}
                to={tab.path}
                title={collapsed ? tab.label : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '11px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  padding: collapsed ? '10px 0' : '10px 12px',
                  borderRadius: radius.md,
                  textDecoration: 'none',
                  background: active ? c.brandTint : 'transparent',
                  border: '1px solid ' + (active ? c.brandTintEdge : 'transparent'),
                }}
              >
                <span style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <tab.Icon
                    size={19}
                    strokeWidth={2}
                    color={active ? c.brand : c.inkMuted}
                    style={{ opacity: active ? 1 : 0.7 }}
                  />
                  {tab.badge > 0 && (
                    <span
                      style={{
                        position: 'absolute', top: '-5px', right: '-8px',
                        background: c.danger, color: c.white,
                        borderRadius: radius.pill, minWidth: '16px', height: '16px',
                        display: 'grid', placeItems: 'center',
                        fontSize: '9.5px', fontWeight: 800, padding: '0 4px',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </span>
                {!collapsed && (
                  <span
                    style={{
                      ...t.body,
                      fontSize: '13.5px',
                      fontWeight: active ? 700 : 500,
                      color: active ? c.brandDeep : c.inkBody,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tab.label}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={glassClass}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 200,
        borderTop: '1px solid ' + c.line,
        boxShadow: '0 -1px 3px rgba(11,18,32,0.05)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {tabs.map((tab) => {
          const active = isActive(tab.path);
          return (
            <Link
              key={tab.path}
              to={tab.path}
              style={{
                flex: 1,
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '3px',
                padding: '9px 4px 8px',
                position: 'relative',
              }}
            >
              {/* the active marker rides the top edge of the tab */}
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: active ? '26px' : '0px',
                  height: '3px',
                  borderRadius: '0 0 3px 3px',
                  background: c.brand,
                  transition: 'width 0.2s ease',
                }}
              />
              <span style={{ position: 'relative', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <tab.Icon
                  size={22}
                  strokeWidth={2}
                  color={active ? c.brand : c.inkMuted}
                  style={{ opacity: active ? 1 : 0.65, transition: 'opacity 0.15s ease' }}
                />
                {tab.badge > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-9px',
                      background: c.danger,
                      color: c.white,
                      borderRadius: radius.pill,
                      minWidth: '16px',
                      height: '16px',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: '9.5px',
                      fontWeight: 800,
                      padding: '0 4px',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </span>
              <span
                style={{
                  ...t.micro,
                  fontSize: '10.5px',
                  fontWeight: active ? 800 : 600,
                  color: active ? c.brandDeep : c.inkMuted,
                }}
              >
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default CustomerNav;